import { formatPrice, formatPercent } from '../../shared/formatting.ts';
import type {
  AggregateGreeks,
  CorrelationMatrix,
  EquityPoint,
  EquityPointHolding,
  ExposureBreakdown,
  GreekBreakdownRow,
  GreekPositionBreakdown,
  PortfolioAccountOption,
  PortfolioAnalysisControls,
  PortfolioAnalysisRenderState,
  PortfolioPnlRange,
  PortfolioSummary,
  SectorExposure,
} from './summary.types.ts';
import {
  identifyStrategyType,
  parseOptionSymbol,
  type StrategyLegInfo,
} from '../brokerage/strategyMatcher.ts';

type ExposureRow = SectorExposure | ExposureBreakdown;
const EXPOSURE_VISIBLE_ROW_LIMIT = 6;

interface TooltipHoldingRow {
  key: string;
  label: string;
  detail: string;
  title: string;
  value: number;
  sortType: EquityPointHolding['type'] | 'strategy';
  legs: TooltipHoldingLeg[];
}

interface TooltipHoldingLeg {
  key: string;
  quantity: number;
  value: number;
}

interface TooltipOptionItem {
  holding: EquityPointHolding;
  parsed: NonNullable<ReturnType<typeof parseOptionSymbol>>;
  direction: 'long' | 'short';
}

export function renderPortfolioAnalysis(
  summary: PortfolioSummary,
  controls: PortfolioAnalysisControls,
  state: PortfolioAnalysisRenderState = {}
): string {
  const pnl = getPnlView(summary, controls);

  return `
    <style>${portfolioAnalysisCss}</style>
    <div class="pa-shell" data-density="${controls.density}">
      ${renderHeader(summary)}
      ${renderKpis(summary, pnl, state)}
      ${renderExposures(summary, state)}
      ${renderGreeks(summary.greeks, summary.greekBreakdown, state)}
      ${renderPnl(summary, pnl, controls, state)}
    </div>
  `;
}

function renderHeader(summary: PortfolioSummary): string {
  const now = new Date();
  const asOf = now.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

  return `
    <div class="pa-appbar">
      <div>
        <div class="pa-brand">Define Your Risk<span>TM</span></div>
        <h1>Portfolio Analysis</h1>
        <p>${summary.positions} positions across connected accounts - last refreshed ${escapeHtml(asOf)}</p>
      </div>
      <div class="pa-actions">
        ${renderAccountSelector(summary.accounts)}
        <button class="pa-refresh" onclick="loadSummary()">Refresh</button>
      </div>
    </div>
  `;
}

function renderAccountSelector(accounts: PortfolioAccountOption[]): string {
  const accountLabel = accounts.length === 1
    ? formatAccountShortLabel(accounts[0])
    : `${accounts.length} connected`;

  return `
    <details class="pa-account-picker">
      <summary class="pa-select">
        <span class="pa-live-dot"></span>
        <span>
          <small>Portfolio</small>
          <strong>Connected accounts</strong>
          <em>${escapeHtml(accountLabel)}</em>
        </span>
      </summary>
      <div class="pa-account-menu" data-account-picker-menu>
        <div class="pa-account-head">
          <span>Connected accounts</span>
          <small>${accounts.length}</small>
        </div>
        ${accounts.map(account => `
          <div class="pa-account-row">
            <span>
              <strong>${escapeHtml(formatAccountName(account))}</strong>
              <small>${escapeHtml(formatAccountDetail(account))}</small>
            </span>
          </div>
        `).join('')}
      </div>
    </details>
  `;
}

function renderKpis(
  summary: PortfolioSummary,
  pnl: PnlView,
  state: PortfolioAnalysisRenderState
): string {
  const dayPositive = summary.dayPL >= 0;
  const periodValue = state.historicalLoading ? 'Loading...' : formatPrice(pnl.periodPnl, true);
  const periodDetail = state.historicalLoading ? state.historicalMessage || 'Building historical P&L' : `${formatPercent(pnl.periodReturnPct)} selected range`;
  const historyCount = summary.equityCurve.length;
  const historyValue = `${historyCount} snapshot${historyCount === 1 ? '' : 's'}`;
  const historyDetail = 'Stored broker NAV history';
  const betaValue = state.currentAnalyticsLoading || state.historicalLoading
    ? 'Loading...'
    : summary.metrics.beta != null ? summary.metrics.beta.toFixed(2) : '—';
  const deltaDetail = state.currentAnalyticsLoading
    ? state.currentAnalyticsMessage || 'Loading option Greeks'
    : `${formatCompactMoney(summary.greeks.deltaDollar)} option delta / $1`;

  return `
    <div class="pa-kpis">
      ${kpi('Net Asset Value', formatPrice(summary.netLiq), `${formatPrice(summary.dayPL, true)} / ${formatPercent(summary.dayPLPct)} today`, dayPositive, true)}
      ${kpi('Period P&L', periodValue, periodDetail, pnl.periodPnl >= 0)}
      ${kpi('History', historyValue, historyDetail, true)}
      ${kpi('Net Beta', betaValue, deltaDetail, summary.greeks.deltaDollar >= 0)}
      ${kpi('Gross / Net', `${summary.grossExposurePct.toFixed(0)}% / ${summary.netExposurePct.toFixed(0)}%`, `Cash ${formatCompactMoney(summary.cash)}`, true)}
    </div>
  `;
}

function renderExposures(summary: PortfolioSummary, state: PortfolioAnalysisRenderState): string {
  const currentMessage = state.currentAnalyticsMessage || 'Loading exposure metadata';

  return `
    <section class="pa-block">
      <div class="pa-block-head">
        <div>
          <h2>Exposures & Characteristics</h2>
          <p>Capital deployment, concentration, and relationship between the largest exposures.</p>
        </div>
      </div>
      <div class="pa-exposure-grid">
        ${state.currentAnalyticsLoading ? renderLoadingCard('Sector exposure', currentMessage) : exposureCard('Sector exposure', '% allocation', filterDisplayableExposureRows(summary.sectors), true)}
        ${state.currentAnalyticsLoading ? renderLoadingCard('Geographic exposure', currentMessage) : exposureCard('Geographic exposure', '% allocation', summary.geographies, true)}
        ${state.currentAnalyticsLoading ? renderLoadingCard('Market cap exposure', currentMessage) : exposureCard('Market cap exposure', '% allocation', filterDisplayableExposureRows(summary.marketCaps), true)}
      </div>
      <div class="pa-exposure-row">
        ${state.currentAnalyticsLoading ? renderLoadingCard('Concentration risk', currentMessage) : renderConcentration(summary)}
        ${state.currentAnalyticsLoading || state.correlationLoading
          ? renderLoadingCard('Correlation matrix', state.currentAnalyticsLoading ? currentMessage : state.correlationMessage || 'Loading historical closes')
          : renderCorrelation(summary.correlationMatrix)}
      </div>
    </section>
  `;
}

function filterDisplayableExposureRows<T extends ExposureRow>(rows: T[]): T[] {
  return rows.filter(row => Math.abs(row.pct) >= 0.05);
}

function exposureCard(title: string, hint: string, rows: ExposureRow[], expandable = false): string {
  const visibleRows = rows.slice(0, EXPOSURE_VISIBLE_ROW_LIMIT);
  const overflowRows = rows.slice(EXPOSURE_VISIBLE_ROW_LIMIT);

  return `
    <article class="pa-card">
      <div class="pa-card-title"><span>${escapeHtml(title)}</span><small>${escapeHtml(hint)}</small></div>
      ${renderExposurePie(title, rows)}
      <div class="pa-bars">
        ${renderExposureRows(visibleRows, expandable)}
        ${overflowRows.length > 0 ? `
          <details class="pa-bars-more">
            <summary>${overflowRows.length} more ${overflowRows.length === 1 ? 'item' : 'items'}</summary>
            <div class="pa-bars-more-list">
              ${renderExposureRows(overflowRows, expandable)}
            </div>
          </details>
        ` : ''}
      </div>
      <div class="pa-legend"><span><i class="pa-you"></i>Your portfolio</span></div>
    </article>
  `;
}

function renderExposureRows(rows: ExposureRow[], expandable: boolean): string {
  return rows.map(row => renderExposureRow(row, expandable)).join('');
}

function renderExposureRow(row: ExposureRow, expandable: boolean): string {
  const hasDetails = expandable && Boolean(row.constituents?.length);
  const rowContent = `
    <span class="pa-bar-name"><i class="pa-pie-dot" style="background:${escapeAttribute(row.color || '#6b7280')}"></i>${escapeHtml(row.name)}</span>
    <span class="pa-bar-value">${formatCompactMoney(row.mv)}</span>
    <span class="pa-bar-pct">${row.pct.toFixed(1)}%</span>
  `;

  if (!hasDetails) {
    return `
      <div class="pa-bar-row" title="${escapeHtml(row.name)}: ${row.pct.toFixed(1)}%">
        ${rowContent}
      </div>
    `;
  }

  return `
    <details class="pa-bar-detail" title="${escapeHtml(row.name)}: ${row.pct.toFixed(1)}%">
      <summary class="pa-bar-row">
        ${rowContent}
      </summary>
      <div class="pa-constituents">
        ${row.constituents!.map(item => `
          <div class="pa-constituent">
            <span class="pa-constituent-symbol">${escapeHtml(item.ticker)}</span>
            <span class="pa-constituent-name" title="${escapeHtml(item.name)}">${escapeHtml(item.name)}</span>
            <span class="pa-constituent-value">${formatPrice(item.mv)}</span>
            <span class="pa-constituent-pct">${item.pct.toFixed(1)}%</span>
          </div>
        `).join('')}
      </div>
    </details>
  `;
}

function renderExposurePie(title: string, rows: ExposureRow[]): string {
  const pieRows = rows.filter(row => row.pct > 0);
  const totalPct = pieRows.reduce((sum, row) => sum + row.pct, 0);
  if (pieRows.length === 0 || totalPct <= 0) {
    return `<div class="pa-empty pa-pie-empty">No exposure to chart.</div>`;
  }

  let offset = 0;
  const segments = pieRows.map(row => {
    const share = (row.pct / totalPct) * 100;
    const segment = `
      <circle
        class="pa-pie-segment"
        cx="50"
        cy="50"
        r="36"
        pathLength="100"
        stroke="${escapeAttribute(row.color || '#6b7280')}"
        stroke-dasharray="${share.toFixed(4)} ${(100 - share).toFixed(4)}"
        stroke-dashoffset="${(-offset).toFixed(4)}"
      ></circle>
    `;
    offset += share;
    return segment;
  }).join('');
  const largest = pieRows.reduce((top, row) => row.pct > top.pct ? row : top, pieRows[0]);

  return `
    <div class="pa-pie-wrap">
      <svg class="pa-pie" viewBox="0 0 100 100" role="img" aria-label="${escapeAttribute(title)} pie chart">
        <circle class="pa-pie-bg" cx="50" cy="50" r="36"></circle>
        ${segments}
        <text x="50" y="48" class="pa-pie-label">${largest.pct.toFixed(1)}%</text>
        <text x="50" y="61" class="pa-pie-sub">largest</text>
      </svg>
      <div class="pa-pie-summary">
        ${pieRows.slice(0, 3).map(row => `
          <span>
            <i style="background:${escapeAttribute(row.color || '#6b7280')}"></i>
            <strong>${escapeHtml(row.name)}</strong>
            <em>${row.pct.toFixed(1)}%</em>
          </span>
        `).join('')}
      </div>
    </div>
  `;
}

function renderConcentration(summary: PortfolioSummary): string {
  const top = summary.topPositions;
  const palette = ['#c9a227', '#d8b340', '#a88a25', '#7c8ecd', '#5e7bb8', '#3fb27f', '#2d8a5f', '#a36bb0', '#7d4d8a', '#9aa0aa'];
  const totalPct = summary.topPositions.reduce((sum, position) => sum + position.pct, 0);
  const restPct = Math.max(0, 100 - totalPct);

  return `
    <article class="pa-card pa-wide">
      <div class="pa-card-title"><span>Concentration risk / top ${top.length} exposures</span><small>${totalPct.toFixed(1)}% of NAV</small></div>
      <div class="pa-stack">
        ${top.map((position, index) => `
          <span style="width:${Math.max(2, position.pct).toFixed(1)}%;background:${palette[index % palette.length]}" title="${escapeHtml(position.ticker)} ${position.pct.toFixed(1)}%">${position.pct >= 5 ? escapeHtml(position.ticker) : ''}</span>
        `).join('')}
        <span class="pa-rest" style="width:${restPct.toFixed(1)}%" title="Remaining positions ${restPct.toFixed(1)}%">Other</span>
      </div>
      <div class="pa-top-table">
        <span class="pa-th">#</span><span class="pa-th">Symbol</span><span class="pa-th">Name</span><span class="pa-th right">% NAV</span><span class="pa-th right">Unrealized</span>
        ${top.map((position, index) => `
          <span class="pa-rank">${String(index + 1).padStart(2, '0')}</span>
          <span class="pa-symbol"><i style="background:${palette[index % palette.length]}"></i>${escapeHtml(position.ticker)}</span>
          <span class="pa-name" title="${escapeHtml(position.name)}">${escapeHtml(position.name)}</span>
          <span class="right">${position.pct.toFixed(1)}%</span>
          <span class="right ${position.dayPL >= 0 ? 'pa-pos' : 'pa-neg'}">${formatPrice(position.dayPL, true)}</span>
        `).join('')}
      </div>
    </article>
  `;
}

function renderCorrelation(matrix: CorrelationMatrix): string {
  if (matrix.labels.length === 0) {
    return `
      <article class="pa-card">
        <div class="pa-card-title"><span>Correlation matrix</span><small>No price history</small></div>
        <div class="pa-empty">Historical closes are needed to calculate correlations.</div>
      </article>
    `;
  }

  return `
    <article class="pa-card pa-correlation-card">
      <div class="pa-card-title"><span>Correlation matrix</span><small>${matrix.lookbackDays} day / ${matrix.labels.length} exposures</small></div>
      <div class="pa-matrix-wrap">
        <table class="pa-matrix">
          <thead><tr><th></th>${matrix.labels.map(label => `<th>${escapeHtml(shortLabel(label))}</th>`).join('')}</tr></thead>
          <tbody>
            ${matrix.values.map((row, rowIndex) => `
              <tr>
                <th>${escapeHtml(shortLabel(matrix.labels[rowIndex]))}</th>
                ${row.map((value, colIndex) => matrixCell(value, matrix.labels[rowIndex], matrix.labels[colIndex])).join('')}
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    </article>
  `;
}

function renderGreeks(
  activeGreeks: AggregateGreeks,
  breakdown: GreekBreakdownRow[],
  state: PortfolioAnalysisRenderState
): string {
  if (state.currentAnalyticsLoading) {
    return `
      <section class="pa-block">
        <div class="pa-block-head">
          <div>
            <h2>Greeks Exposure</h2>
            <p>All currently held options, including spread legs, with signed exposure preserved.</p>
          </div>
        </div>
        ${renderLoadingCard('Greeks exposure', state.currentAnalyticsMessage || 'Loading option Greeks')}
      </section>
    `;
  }

  const rows = breakdown.filter(row => row.type !== 'equity' && row.count > 0);
  const gaugeRanges = getGreekGaugeRanges(rows, activeGreeks);

  return `
    <section class="pa-block">
      <div class="pa-block-head">
        <div>
          <h2>Greeks Exposure</h2>
          <p>All currently held options, including spread legs, with signed exposure preserved.</p>
        </div>
      </div>
      <article class="pa-card pa-greeks-card">
        <div class="pa-gauges">
          ${gauge('Delta', 'Delta / $1 move', activeGreeks.deltaDollar, gaugeRanges.deltaDollar, 'Estimated option P&L for a $1 move in each underlying')}
          ${gauge('Gamma', 'Gamma dollars', activeGreeks.gammaDollar, gaugeRanges.gammaDollar, 'Estimated P&L from a 1% underlying move')}
          ${gauge('Theta', 'Theta / day', activeGreeks.theta, gaugeRanges.theta, 'Daily time decay or accrual')}
          ${gauge('Vega', 'Vega / vol pt', activeGreeks.vega, gaugeRanges.vega, 'Dollar impact per 1 vol point')}
          ${gauge('Rho', 'Rho / rate pt', activeGreeks.rho, gaugeRanges.rho, 'Dollar impact per 1 rate point')}
        </div>
        <div class="pa-greek-table-wrap">
          <div class="pa-greek-table" role="table">
            <div class="pa-greek-head" role="row">
              <span>Trade type</span><span>Delta / $1</span><span>Gamma $</span><span>Theta / day</span><span>Vega</span><span>Rho</span>
            </div>
            ${rows.map(renderGreekBreakdownRow).join('')}
            <div class="pa-greek-total" role="row">
              <span>All options</span>
              <span>${moneyCell(activeGreeks.deltaDollar)}</span>
              <span>${moneyCell(activeGreeks.gammaDollar)}</span>
              <span>${moneyCell(activeGreeks.theta)}</span>
              <span>${moneyCell(activeGreeks.vega)}</span>
              <span>${moneyCell(activeGreeks.rho)}</span>
            </div>
          </div>
        </div>
      </article>
    </section>
  `;
}

function renderGreekBreakdownRow(row: GreekBreakdownRow): string {
  return `
    <details class="pa-greek-type-row">
      <summary class="pa-greek-summary">
        <span class="pa-greek-label"><span class="pa-tag ${row.type}">${row.type}</span>${escapeHtml(row.label)} <small>${row.count}</small></span>
        <span>${moneyCell(row.greeks.deltaDollar)}</span>
        <span>${moneyCell(row.greeks.gammaDollar)}</span>
        <span>${moneyCell(row.greeks.theta)}</span>
        <span>${moneyCell(row.greeks.vega)}</span>
        <span>${moneyCell(row.greeks.rho)}</span>
      </summary>
      <div class="pa-greek-position-list">
        ${row.positions.length > 0
          ? row.positions.map(renderGreekPositionRow).join('')
          : '<div class="pa-greek-empty">No option positions in this bucket.</div>'}
      </div>
    </details>
  `;
}

function renderGreekPositionRow(position: GreekPositionBreakdown): string {
  return `
    <div class="pa-greek-position-row">
      <span class="pa-greek-position-name">
        <strong>${escapeHtml(shortOptionLabel(position.name))}</strong>
        <small>${escapeHtml(position.underlyingSymbol)} / ${formatContracts(position.contracts)} / ${formatPrice(position.marketValue, true)}${position.hasGreeks ? '' : ' / Greeks missing'}</small>
      </span>
      <span>${moneyCell(position.greeks.deltaDollar)}</span>
      <span>${moneyCell(position.greeks.gammaDollar)}</span>
      <span>${moneyCell(position.greeks.theta)}</span>
      <span>${moneyCell(position.greeks.vega)}</span>
      <span>${moneyCell(position.greeks.rho)}</span>
    </div>
  `;
}

function renderPnl(
  summary: PortfolioSummary,
  pnl: PnlView,
  controls: PortfolioAnalysisControls,
  state: PortfolioAnalysisRenderState
): string {
  return `
    <section class="pa-block">
      <div class="pa-block-head">
        <div>
          <h2>Net Portfolio Value</h2>
          <p>Stored broker NAV snapshots over time. Includes deposits, withdrawals, and transfers.</p>
        </div>
        <div class="pa-pnl-controls">
          <div class="pa-segment">
            ${button('$', controls.pnlUnit === 'dollar', 'setPortfolioPnlUnit("dollar")')}
            ${button('%', controls.pnlUnit === 'percent', 'setPortfolioPnlUnit("percent")')}
          </div>
          <div class="pa-segment">
            ${rangeButton('1M', controls.pnlRange)}
            ${rangeButton('3M', controls.pnlRange)}
            ${rangeButton('YTD', controls.pnlRange)}
            ${rangeButton('1Y', controls.pnlRange)}
            ${rangeButton('All', controls.pnlRange, 'ALL')}
          </div>
        </div>
      </div>
      <article class="pa-card pa-pnl-card">
        ${state.historicalLoading ? renderPnlLoadingState(state) : `
          <div class="pa-pnl-stats">
            <div><strong class="${pnl.periodPnl >= 0 ? 'pa-pos' : 'pa-neg'}">${formatPrice(pnl.periodPnl, true)}</strong><small>Period P&L</small></div>
            <div><strong class="${pnl.periodReturnPct >= 0 ? 'pa-pos' : 'pa-neg'}">${formatPercent(pnl.periodReturnPct)}</strong><small>Period return</small></div>
            <div><strong>${summary.equityCurve.length}</strong><small>Snapshots</small></div>
            <div><strong>Broker NAV</strong><small>Source</small></div>
          </div>
          ${pnlChart(pnl)}
          ${renderPriceCoverageNote(summary)}
          <div class="pa-transfer-note">This simple view includes deposits, withdrawals, and transfers. It does not reconstruct historical holdings or option marks.</div>
          <div class="pa-legend"><span><i class="pa-you"></i>Your portfolio</span></div>
        `}
      </article>
    </section>
  `;
}

function renderLoadingCard(title: string, message: string): string {
  return `
    <article class="pa-card">
      <div class="pa-card-title"><span>${escapeHtml(title)}</span><small>Loading</small></div>
      <div class="pa-loading-state">
        <span class="pa-spinner" aria-hidden="true"></span>
        <span>${escapeHtml(message)}</span>
      </div>
    </article>
  `;
}

function renderPnlLoadingState(state: PortfolioAnalysisRenderState): string {
  return `
    <div class="pa-pnl-loading">
      <div class="pa-pnl-skeleton-stats">
        <span></span><span></span><span></span><span></span>
      </div>
      <div class="pa-chart-skeleton">
        <span class="pa-spinner" aria-hidden="true"></span>
        <strong>Building historical portfolio value</strong>
        <small>${escapeHtml(state.historicalMessage || 'Fetching activities, historical prices, and option marks')}</small>
      </div>
    </div>
  `;
}

interface PnlView {
  points: EquityPoint[];
  benchmarkPoints: EquityPoint[];
  periodPnl: number;
  periodReturnPct: number;
  benchmarkReturnPct: number;
  alphaPct: number;
  unit: PortfolioAnalysisControls['pnlUnit'];
}

function getPnlView(summary: PortfolioSummary, controls: PortfolioAnalysisControls): PnlView {
  const points = filterCurve(summary.equityCurve, controls.pnlRange);
  const benchmarkCurve = summary.benchmarkCurve;
  const benchmarkByDate = new Map(benchmarkCurve.map(point => [point.date, point]));
  const benchmarkPoints = points
    .map(point => benchmarkByDate.get(point.date))
    .filter((point): point is EquityPoint => Boolean(point));

  const first = points[0];
  const last = points[points.length - 1] || first;
  const benchmarkFirst = benchmarkPoints[0];
  const benchmarkLast = benchmarkPoints[benchmarkPoints.length - 1] || benchmarkFirst;
  const periodPnl = first && last ? last.value - first.value : 0;
  const periodReturnPct = first && last && first.value > 0
    ? ((last.value - first.value) / first.value) * 100
    : 0;
  const benchmarkReturnPct = benchmarkFirst && benchmarkLast && benchmarkFirst.value > 0
    ? ((benchmarkLast.value - benchmarkFirst.value) / benchmarkFirst.value) * 100
    : 0;

  return {
    points,
    benchmarkPoints,
    periodPnl,
    periodReturnPct,
    benchmarkReturnPct,
    alphaPct: periodReturnPct - benchmarkReturnPct,
    unit: controls.pnlUnit,
  };
}

function renderPriceCoverageNote(summary: PortfolioSummary): string {
  const coverage = summary.priceCoverage;
  if (!coverage || coverage.candidateDates === 0) return '';

  const skippedSymbols = coverage.skippedSymbols
    .slice(0, 4)
    .map(item => `${item.symbol} (${item.count})`)
    .join(', ');
  const lowConfidence = coverage.lowConfidenceOptionMarks > 0
    ? `${coverage.lowConfidenceOptionMarks}/${coverage.totalOptionMarks || coverage.lowConfidenceOptionMarks} option marks (${formatPercent(optionMarkPct(coverage.lowConfidenceOptionMarks, coverage.totalOptionMarks || coverage.lowConfidenceOptionMarks))}) were low-confidence.`
    : '';
  const fallback = coverage.fallbackOptionMarks > 0
    ? `${coverage.fallbackOptionMarks} missing exact option mark${coverage.fallbackOptionMarks === 1 ? '' : 's'} valued from nearby marks.`
    : '';
  const missingRequired = coverage.skippedRequiredSymbols.length > 0
    ? `Missing valuation marks: ${coverage.skippedRequiredSymbols.map(item => `${item.symbol} ${item.type} (${item.count})`).join(', ')}.`
    : '';
  const sources = formatCoverageSources(coverage.sourceCounts);
  const skipped = coverage.skippedOptionMarkDates > 0
    ? `${coverage.skippedOptionMarkDates} candidate date${coverage.skippedOptionMarkDates === 1 ? '' : 's'} skipped because exact option marks were unavailable${skippedSymbols ? `: ${skippedSymbols}` : ''}.`
    : '';

  if (!skipped && !lowConfidence && !fallback && !missingRequired) return '';

  return `
    <div class="pa-mark-note">
      <strong>Option mark coverage:</strong>
      ${coverage.renderedDates}/${coverage.candidateDates} dates rendered.
      ${escapeHtml(sources)}
      ${escapeHtml(skipped)}
      ${escapeHtml(lowConfidence)}
      ${escapeHtml(fallback)}
      ${escapeHtml(missingRequired)}
    </div>
  `;
}

function optionMarkPct(count: number, total: number): number {
  return total > 0 ? (count / total) * 100 : 0;
}

function formatCoverageSources(sourceCounts: PortfolioSummary['priceCoverage']['sourceCounts']): string {
  const labels: Record<string, string> = {
    fmv: 'FMV',
    quote_mid: 'quote midpoint',
    trade_close: 'trade close',
    broker_current: 'broker current',
    historical_close: 'historical close',
    interpolated: 'interpolated',
    nearest_option_mark: 'nearby mark',
  };
  const parts = Object.entries(sourceCounts)
    .filter(([, count]) => Number(count) > 0)
    .map(([source, count]) => `${labels[source] || source}: ${count}`);

  return parts.length > 0 ? `Sources: ${parts.join(', ')}.` : '';
}

function filterCurve(points: EquityPoint[], range: PortfolioPnlRange): EquityPoint[] {
  if (points.length === 0 || range === 'ALL') return points;

  const lastDate = new Date(points[points.length - 1].date);
  const cutoff = new Date(lastDate);
  if (range === '1M') cutoff.setMonth(cutoff.getMonth() - 1);
  if (range === '3M') cutoff.setMonth(cutoff.getMonth() - 3);
  if (range === '1Y') cutoff.setFullYear(cutoff.getFullYear() - 1);
  if (range === 'YTD') {
    cutoff.setMonth(0, 1);
    cutoff.setHours(0, 0, 0, 0);
  }

  return points.filter(point => new Date(point.date) >= cutoff);
}

function pnlChart(pnl: PnlView): string {
  if (pnl.points.length < 2) {
    return `<div class="pa-empty">One NAV snapshot saved. The line will appear after at least two snapshot dates are available.</div>`;
  }

  const width = 1000;
  const height = 360;
  const left = 70;
  const right = 18;
  const top = 18;
  const bottom = 36;
  const plotWidth = width - left - right;
  const plotHeight = height - top - bottom;

  const firstPortfolio = pnl.points[0].value;
  const firstBenchmark = pnl.benchmarkPoints[0]?.value || 0;
  const portfolioValues = pnl.unit === 'percent'
    ? pnl.points.map(point => (point.value / firstPortfolio - 1) * 100)
    : pnl.points.map(point => point.value);
  const benchmarkValues = pnl.benchmarkPoints.length >= 2
    ? (pnl.unit === 'percent'
      ? pnl.benchmarkPoints.map(point => (point.value / firstBenchmark - 1) * 100)
      : pnl.benchmarkPoints.map(point => point.value))
    : [];

  const allValues = portfolioValues.concat(benchmarkValues);
  const min = Math.min(...allValues);
  const max = Math.max(...allValues);
  const padding = (max - min) * 0.12 || 1;
  const yMin = min - padding;
  const yMax = max + padding;
  const yRange = yMax - yMin || 1;
  const xAt = (index: number, length: number) => left + (index / Math.max(1, length - 1)) * plotWidth;
  const yAt = (value: number) => top + (1 - (value - yMin) / yRange) * plotHeight;
  const portfolioPath = portfolioValues.map((value, index) => `${index === 0 ? 'M' : 'L'} ${xAt(index, portfolioValues.length).toFixed(1)} ${yAt(value).toFixed(1)}`).join(' ');
  const benchmarkPath = benchmarkValues.map((value, index) => `${index === 0 ? 'M' : 'L'} ${xAt(index, benchmarkValues.length).toFixed(1)} ${yAt(value).toFixed(1)}`).join(' ');
  const fillPath = `${portfolioPath} L ${xAt(portfolioValues.length - 1, portfolioValues.length).toFixed(1)} ${top + plotHeight} L ${left} ${top + plotHeight} Z`;
  const yTicks = Array.from({ length: 5 }, (_, index) => yMin + (index / 4) * (yMax - yMin));
  const xTicks = Array.from({ length: Math.min(6, pnl.points.length) }, (_, index) => {
    const pointIndex = Math.round((pnl.points.length - 1) * (index / Math.max(1, Math.min(6, pnl.points.length) - 1)));
    return { pointIndex, point: pnl.points[pointIndex] };
  });

  return `
    <div class="pa-chart">
      <svg viewBox="0 0 ${width} ${height}" preserveAspectRatio="none" role="img" aria-label="Net portfolio value over time">
        <defs>
          <linearGradient id="paPnlGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="var(--pa-accent)" stop-opacity=".2"/>
            <stop offset="100%" stop-color="var(--pa-accent)" stop-opacity="0"/>
          </linearGradient>
        </defs>
        ${yTicks.map(value => {
          const y = yAt(value);
          return `<line x1="${left}" x2="${width - right}" y1="${y}" y2="${y}" stroke="var(--pa-border)" stroke-dasharray="2 4"/><text x="${left - 10}" y="${y + 3}" text-anchor="end">${formatAxis(value, pnl.unit)}</text>`;
        }).join('')}
        ${xTicks.map(({ pointIndex, point }) => `<text x="${xAt(pointIndex, pnl.points.length)}" y="${height - 12}" text-anchor="middle">${formatDateLabel(point.date)}</text>`).join('')}
        ${benchmarkPath ? `<path d="${benchmarkPath}" fill="none" stroke="var(--pa-text-2)" stroke-width="1.5" stroke-dasharray="4 4"/>` : ''}
        <path d="${fillPath}" fill="url(#paPnlGradient)"/>
        <path d="${portfolioPath}" fill="none" stroke="var(--pa-accent)" stroke-width="2.2" stroke-linejoin="round"/>
      </svg>
      <div class="pa-chart-overlay">
        ${pnl.points.map((point, index) => renderPnlPointHotspot(
          point,
          pnl.points,
          index,
          (xAt(index, portfolioValues.length) / width) * 100,
          (yAt(portfolioValues[index]) / height) * 100
        )).join('')}
      </div>
    </div>
  `;
}

function renderPnlPointHotspot(
  point: EquityPoint,
  points: EquityPoint[],
  index: number,
  xPct: number,
  yPct: number
): string {
  const alignRight = xPct > 68;
  const holdings = point.holdings || [];
  const aria = `${formatDateLabel(point.date)} portfolio value ${formatPrice(point.value)}`;

  return `
    <div
      class="pa-chart-hit ${alignRight ? 'right' : ''}"
      data-pnl-point="${index}"
      tabindex="0"
      role="button"
      aria-pressed="false"
      aria-label="${escapeAttribute(aria)}"
      style="--x:${xPct.toFixed(3)}%;--y:${yPct.toFixed(3)}%"
    >
      <span class="pa-chart-dot"></span>
      <div class="pa-chart-tooltip">
        <div class="pa-chart-tooltip-head">
          <strong>${escapeHtml(formatDateLabel(point.date))}</strong>
          <span>${formatPrice(point.value)}</span>
          <button type="button" class="pa-chart-unpin" data-pnl-unpin aria-label="Close pinned day">Close</button>
        </div>
        <div class="pa-chart-tooltip-body">
          ${holdings.length > 0 ? renderPnlHoldingRows(point, points, index) : '<div class="pa-chart-tooltip-empty">No position breakdown for this date.</div>'}
        </div>
      </div>
    </div>
  `;
}

function renderPnlHoldingRows(point: EquityPoint, points: EquityPoint[], index: number): string {
  const previousPoint = points[index - 1] || null;
  const priorWeekPoint = findPriorPointByCalendarDays(points, index, 7);
  const rows = groupTooltipHoldings(point.holdings || []);
  const previousHoldings = holdingRowMap(groupTooltipHoldings(previousPoint?.holdings || []));
  const priorWeekHoldings = holdingRowMap(groupTooltipHoldings(priorWeekPoint?.holdings || []));

  return `
    <div class="pa-chart-tooltip-grid">
      <span>Holding</span><span>Value</span><span>1D</span><span>1W</span>
      ${rows.map(row => {
        const oneDayChange = previousPoint ? markToMarketChange(row, previousHoldings.get(row.key) || null) : null;
        const oneWeekChange = priorWeekPoint ? markToMarketChange(row, priorWeekHoldings.get(row.key) || null) : null;
        return `
          <span class="pa-chart-holding" title="${escapeAttribute(row.title)}">
            <strong>${escapeHtml(row.label)}</strong>
            <small>${escapeHtml(row.detail)}</small>
          </span>
          <span class="${valueClass(row.value)}">${formatPrice(row.value)}</span>
          <span class="${valueClass(oneDayChange)}">${formatChange(oneDayChange)}</span>
          <span class="${valueClass(oneWeekChange)}">${formatChange(oneWeekChange)}</span>
        `;
      }).join('')}
    </div>
  `;
}

function holdingRowMap(holdings: TooltipHoldingRow[]): Map<string, TooltipHoldingRow> {
  const result = new Map<string, TooltipHoldingRow>();
  for (const holding of holdings) {
    result.set(holding.key, holding);
  }
  return result;
}

function markToMarketChange(row: TooltipHoldingRow, previousRow: TooltipHoldingRow | null): number | null {
  if (!previousRow || row.sortType === 'cash' || row.sortType === 'adjustment') return null;

  let change = 0;
  for (const leg of row.legs) {
    const previousLeg = previousRow.legs.find(item => item.key === leg.key);
    if (!previousLeg || Math.abs(leg.quantity) < 0.000001 || Math.abs(previousLeg.quantity) < 0.000001) {
      return null;
    }
    if (Math.sign(leg.quantity) !== Math.sign(previousLeg.quantity)) {
      return null;
    }

    const currentUnitValue = leg.value / leg.quantity;
    const previousUnitValue = previousLeg.value / previousLeg.quantity;
    if (!Number.isFinite(currentUnitValue) || !Number.isFinite(previousUnitValue)) {
      return null;
    }

    change += leg.quantity * (currentUnitValue - previousUnitValue);
  }

  return Number.isFinite(change) ? change : null;
}

function groupTooltipHoldings(holdings: EquityPointHolding[]): TooltipHoldingRow[] {
  const optionItems: TooltipOptionItem[] = [];
  const rows: TooltipHoldingRow[] = [];

  for (const holding of holdings) {
    if (holding.type === 'option') {
      const parsed = parseOptionSymbol(holding.symbol);
      if (parsed) {
        optionItems.push({
          holding,
          parsed,
          direction: holding.quantity >= 0 ? 'long' : 'short',
        });
        continue;
      }
    }

    rows.push(tooltipRowFromHolding(holding));
  }

  rows.push(...groupOptionTooltipItems(optionItems));
  return rows.sort((a, b) => {
    if (a.sortType === 'cash' && b.sortType !== 'cash') return 1;
    if (b.sortType === 'cash' && a.sortType !== 'cash') return -1;
    if (a.sortType === 'adjustment' && b.sortType !== 'adjustment') return 1;
    if (b.sortType === 'adjustment' && a.sortType !== 'adjustment') return -1;
    return Math.abs(b.value) - Math.abs(a.value);
  });
}

function groupOptionTooltipItems(items: TooltipOptionItem[]): TooltipHoldingRow[] {
  const rows: TooltipHoldingRow[] = [];
  const used = new Set<number>();
  const sorted = items
    .map((item, index) => ({ ...item, index }))
    .sort((a, b) =>
      a.parsed.underlying.localeCompare(b.parsed.underlying) ||
      a.parsed.type.localeCompare(b.parsed.type) ||
      a.parsed.expiration.localeCompare(b.parsed.expiration) ||
      a.parsed.strike - b.parsed.strike
    );

  for (const item of sorted) {
    if (used.has(item.index)) continue;

    const candidate = sorted
      .filter(other =>
        other.index !== item.index &&
        !used.has(other.index) &&
        other.parsed.underlying === item.parsed.underlying &&
        other.parsed.type === item.parsed.type &&
        other.direction !== item.direction
      )
      .sort((a, b) => optionPairScore(item, a) - optionPairScore(item, b))[0];

    if (candidate) {
      used.add(item.index);
      used.add(candidate.index);
      rows.push(tooltipRowFromOptionGroup([item, candidate]));
      continue;
    }

    used.add(item.index);
    rows.push(tooltipRowFromHolding(item.holding));
  }

  return rows;
}

function optionPairScore(a: TooltipOptionItem, b: TooltipOptionItem): number {
  const sameExpiration = a.parsed.expiration === b.parsed.expiration ? 0 : 10_000;
  const expirationDistance = Math.abs(new Date(a.parsed.expiration).getTime() - new Date(b.parsed.expiration).getTime()) / 86_400_000;
  const quantityDistance = Math.abs(Math.abs(a.holding.quantity) - Math.abs(b.holding.quantity)) * 20;
  const strikeDistance = Math.abs(a.parsed.strike - b.parsed.strike);
  return sameExpiration + expirationDistance + quantityDistance + strikeDistance;
}

function tooltipRowFromHolding(holding: EquityPointHolding): TooltipHoldingRow {
  return {
    key: holdingKey(holding),
    label: shortTooltipHoldingLabel(holding),
    detail: `${formatHoldingQuantity(holding)}${stripHtml(formatHoldingMark(holding))}`,
    title: holding.name,
    value: holding.value,
    sortType: holding.type,
    legs: [tooltipLegFromHolding(holding)],
  };
}

function tooltipRowFromOptionGroup(items: TooltipOptionItem[]): TooltipHoldingRow {
  const sorted = [...items].sort((a, b) =>
    a.parsed.expiration.localeCompare(b.parsed.expiration) ||
    a.parsed.strike - b.parsed.strike ||
    a.direction.localeCompare(b.direction)
  );
  const strategyLegs: StrategyLegInfo[] = sorted.map(item => ({
    parsed: item.parsed,
    direction: item.direction,
    quantity: Math.abs(item.holding.quantity),
  }));
  const label = `${sorted[0].parsed.underlying} ${identifyStrategyType(strategyLegs)}`;
  const legSummary = sorted.map(item => optionLegLabel(item)).join(' / ');
  const value = sorted.reduce((sum, item) => sum + item.holding.value, 0);

  return {
    key: `strategy:${sorted.map(item => item.holding.symbol).sort().join('|')}`,
    label,
    detail: legSummary,
    title: sorted.map(item => item.holding.name).join(' / '),
    value,
    sortType: 'strategy',
    legs: sorted.map(item => tooltipLegFromHolding(item.holding)),
  };
}

function tooltipLegFromHolding(holding: EquityPointHolding): TooltipHoldingLeg {
  return {
    key: holdingKey(holding),
    quantity: holding.quantity,
    value: holding.value,
  };
}

function optionLegLabel(item: TooltipOptionItem): string {
  const quantity = Math.abs(item.holding.quantity);
  const prefix = item.holding.quantity < 0 ? '-' : '+';
  const type = item.parsed.type === 'CALL' ? 'C' : 'P';
  return `${prefix}${formatQuantityValue(quantity)} $${formatStrike(item.parsed.strike)}${type}`;
}

function formatQuantityValue(quantity: number): string {
  return Number.isInteger(quantity) ? String(quantity) : quantity.toFixed(3).replace(/\.?0+$/, '');
}

function stripHtml(value: string): string {
  return value.replace(/<[^>]*>/g, '');
}

function holdingKey(holding: EquityPointHolding): string {
  return `${holding.type}:${holding.symbol}`;
}

function findPriorPointByCalendarDays(points: EquityPoint[], index: number, days: number): EquityPoint | null {
  const current = points[index];
  if (!current) return null;

  const target = new Date(`${current.date}T00:00:00Z`);
  target.setUTCDate(target.getUTCDate() - days);
  const targetKey = target.toISOString().split('T')[0];

  for (let i = index - 1; i >= 0; i -= 1) {
    if (points[i].date <= targetKey) return points[i];
  }

  return null;
}

function valueClass(value: number | null): string {
  if (value == null || Math.abs(value) < 0.5) return 'pa-muted';
  return value >= 0 ? 'pa-pos' : 'pa-neg';
}

function formatChange(value: number | null): string {
  if (value == null) return '—';
  return formatPrice(value, true);
}

function shortTooltipHoldingLabel(holding: EquityPointHolding): string {
  if (holding.type === 'cash') return 'Cash';
  if (holding.type === 'adjustment') return 'Transfer adjustment';
  return shortOptionLabel(holding.symbol);
}

function formatHoldingQuantity(holding: EquityPointHolding): string {
  if (holding.type === 'cash' || holding.type === 'adjustment') return holding.type;

  const abs = Math.abs(holding.quantity);
  const quantity = Number.isInteger(abs)
    ? abs.toFixed(0)
    : abs.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
  const prefix = holding.quantity < 0 ? '-' : '+';
  const unit = holding.type === 'option'
    ? ` contract${abs === 1 ? '' : 's'}`
    : ` share${abs === 1 ? '' : 's'}`;
  return `${prefix}${quantity}${unit}`;
}

function formatHoldingMark(holding: EquityPointHolding): string {
  if (!holding.markSource || holding.type === 'cash' || holding.type === 'adjustment') return '';

  const source = holding.markSource.replace(/_/g, ' ');
  const confidence = holding.markConfidence === 'low' ? ' low confidence' : '';
  const date = holding.markDate ? ` ${holding.markDate}` : '';
  return ` / ${escapeHtml(source)}${escapeHtml(confidence)}${escapeHtml(date)}`;
}

function kpi(label: string, value: string, sub: string, positive: boolean, primary = false): string {
  return `
    <div class="pa-kpi ${primary ? 'primary' : ''}">
      <small>${escapeHtml(label)}</small>
      <strong class="${positive ? 'pa-pos-text' : 'pa-neg-text'}">${escapeHtml(value)}</strong>
      <span>${escapeHtml(sub)}</span>
    </div>
  `;
}

function gauge(label: string, metric: string, value: number, range: number, description: string): string {
  const normalized = Math.max(-1, Math.min(1, value / range));
  const rotation = normalized * 90;
  const fill = Math.abs(normalized) * 126;
  const stroke = value >= 0 ? 'var(--pa-accent)' : 'var(--pa-neg)';

  return `
    <div class="pa-gauge" title="${escapeHtml(description)}">
      <small>${escapeHtml(metric)}</small>
      <svg viewBox="0 0 140 82">
        <path d="M 18 66 A 52 52 0 0 1 122 66" pathLength="126" class="track"/>
        <path d="M 70 14 A 52 52 0 0 ${value >= 0 ? '1' : '0'} ${value >= 0 ? '122' : '18'} 66" pathLength="126" style="stroke:${stroke};stroke-dasharray:${fill} 126"/>
        <line x1="70" y1="66" x2="70" y2="22" style="transform:rotate(${rotation}deg);transform-origin:70px 66px"/>
        <circle cx="70" cy="66" r="3"/>
      </svg>
      <strong class="${value >= 0 ? 'pa-pos' : 'pa-neg'}">${formatPrice(value, true)}</strong>
      <span>${escapeHtml(label)}</span>
    </div>
  `;
}

function matrixCell(value: number | null, left: string, right: string): string {
  if (value == null) {
    return `<td class="missing" title="${escapeHtml(left)} x ${escapeHtml(right)}: insufficient data">—</td>`;
  }

  const intensity = Math.min(1, Math.abs(value));
  const background = value >= 0
    ? `rgba(140,77,58,${0.22 + intensity * 0.58})`
    : `rgba(58,77,140,${0.22 + intensity * 0.58})`;
  const text = Math.abs(value) >= 0.55 ? '#fff' : 'var(--pa-text-1)';

  return `<td style="background:${background};color:${text}" title="${escapeHtml(left)} x ${escapeHtml(right)}: ${value.toFixed(2)}">${value.toFixed(2)}</td>`;
}

function moneyCell(value: number): string {
  if (Math.abs(value) < 0.5) return `<span class="pa-muted">$0</span>`;
  return `<span class="${value >= 0 ? 'pa-pos' : 'pa-neg'}">${formatPrice(value, true)}</span>`;
}

function rangeButton(label: string, activeRange: PortfolioPnlRange, value?: PortfolioPnlRange): string {
  const range = value || label as PortfolioPnlRange;
  return button(label, activeRange === range, `setPortfolioPnlRange("${range}")`);
}

function button(label: string, active: boolean, action: string): string {
  return `<button class="${active ? 'active' : ''}" onclick='${escapeAttribute(action)}'>${escapeHtml(label)}</button>`;
}

function getGreekGaugeRanges(rows: GreekBreakdownRow[], activeGreeks: AggregateGreeks): Pick<AggregateGreeks, 'deltaDollar' | 'gammaDollar' | 'theta' | 'vega' | 'rho'> {
  return {
    deltaDollar: greekRange(rows, activeGreeks, 'deltaDollar'),
    gammaDollar: greekRange(rows, activeGreeks, 'gammaDollar'),
    theta: greekRange(rows, activeGreeks, 'theta'),
    vega: greekRange(rows, activeGreeks, 'vega'),
    rho: greekRange(rows, activeGreeks, 'rho'),
  };
}

function greekRange(
  rows: GreekBreakdownRow[],
  activeGreeks: AggregateGreeks,
  key: keyof Pick<AggregateGreeks, 'deltaDollar' | 'gammaDollar' | 'theta' | 'vega' | 'rho'>
): number {
  const rowMax = Math.max(0, ...rows.map(row => Math.abs(row.greeks[key])));
  const active = Math.abs(activeGreeks[key]);
  return Math.max(1, rowMax, active);
}

function shortLabel(label: string): string {
  return label.length > 5 ? label.slice(0, 5) : label;
}

function shortOptionLabel(label: string): string {
  const compactOcc = label.match(/^([A-Z.]+)(\d{6})([CP])(\d{8})$/);
  if (!compactOcc) return label;

  const [, underlying, yymmdd, type, strikeRaw] = compactOcc;
  const month = yymmdd.slice(2, 4);
  const day = yymmdd.slice(4, 6);
  const strike = Number(strikeRaw) / 1000;
  return `${underlying} ${month}/${day} $${formatStrike(strike)} ${type === 'C' ? 'CALL' : 'PUT'}`;
}

function formatStrike(value: number): string {
  return Number.isInteger(value) ? value.toFixed(0) : value.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
}

function formatContracts(contracts: number): string {
  const abs = Math.abs(contracts);
  const quantity = Number.isInteger(abs) ? abs.toFixed(0) : abs.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
  return `${contracts < 0 ? '-' : '+'}${quantity} contract${abs === 1 ? '' : 's'}`;
}

function formatCompactMoney(value: number): string {
  const sign = value < 0 ? '-' : '';
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`;
  return `${sign}$${Math.round(abs)}`;
}

function formatAxis(value: number, unit: PortfolioAnalysisControls['pnlUnit']): string {
  if (unit === 'percent') return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  return formatCompactMoney(value);
}

function formatDateLabel(date: string): string {
  const parsed = new Date(date);
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatAccountName(account: PortfolioAccountOption): string {
  const institution = account.institutionName.trim();
  const name = account.name.trim();
  if (!institution) return name || 'Account';
  if (!name || name === institution) return institution;
  return `${institution} - ${name}`;
}

function formatAccountDetail(account: PortfolioAccountOption): string {
  const parts: string[] = [];
  if (account.mask) parts.push(`••••${account.mask}`);
  if (account.balance != null) parts.push(formatPrice(account.balance));
  return parts.join(' · ') || 'No account number';
}

function formatAccountShortLabel(account: PortfolioAccountOption): string {
  return account.mask ? `${account.name} ••••${account.mask}` : account.name;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function escapeAttribute(value: string): string {
  return escapeHtml(value);
}

const portfolioAnalysisCss = `
.pa-shell{--pa-bg-0:#0a0a0b;--pa-bg-1:#111113;--pa-bg-2:#16161a;--pa-bg-3:#1d1d22;--pa-border:#28282e;--pa-border-strong:#34343b;--pa-text-0:#f5f5f7;--pa-text-1:#c9c9ce;--pa-text-2:#8a8a92;--pa-text-3:#606068;--pa-accent:#c9a227;--pa-accent-light:#e4c65b;--pa-accent-soft:rgba(201,162,39,.13);--pa-pos:#3fb27f;--pa-neg:#e26d6d;--pa-pad:22px;--pa-row:42px;background:var(--pa-bg-0);color:var(--pa-text-0);font-family:Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;min-height:calc(100vh - 120px);margin:-24px;padding:24px;border-radius:0}
.pa-shell[data-density="compact"]{--pa-pad:16px;--pa-row:34px}
.pa-shell *{box-sizing:border-box}
.pa-shell button{font:inherit}
.pa-appbar{display:flex;align-items:flex-start;justify-content:space-between;gap:18px;max-width:1440px;margin:0 auto 22px}
.pa-brand{font-size:12px;color:var(--pa-accent-light);font-weight:700;letter-spacing:.12em;text-transform:uppercase;margin-bottom:8px}.pa-brand span{font-size:8px;margin-left:3px;vertical-align:super}
.pa-appbar h1{font-size:32px;line-height:1.1;font-weight:300;letter-spacing:-.025em;margin:0}.pa-appbar p{margin:5px 0 0;color:var(--pa-text-2);font-size:13px}
.pa-actions{display:flex;align-items:center;justify-content:flex-end;gap:10px;flex-wrap:wrap}.pa-select{display:flex;align-items:center;gap:10px;background:var(--pa-bg-2);border:1px solid var(--pa-border);border-radius:8px;padding:7px 12px}.pa-select small{display:block;font-size:10px;text-transform:uppercase;letter-spacing:.1em;color:var(--pa-text-2);line-height:1}.pa-select strong{display:block;font-size:13px;font-weight:600}.pa-select em{display:block;margin-top:2px;color:var(--pa-text-2);font-size:11px;font-style:normal}.pa-live-dot{width:7px;height:7px;border-radius:99px;background:var(--pa-pos);box-shadow:0 0 0 4px rgba(63,178,127,.15)}
.pa-account-picker{position:relative}.pa-account-picker summary{list-style:none;cursor:pointer}.pa-account-picker summary::-webkit-details-marker{display:none}.pa-account-menu{position:absolute;right:0;top:calc(100% + 8px);z-index:20;width:360px;max-width:calc(100vw - 32px);background:var(--pa-bg-1);border:1px solid var(--pa-border-strong);border-radius:10px;box-shadow:0 18px 40px rgba(0,0,0,.45);padding:10px}.pa-account-head{display:flex;justify-content:space-between;align-items:center;padding:4px 4px 9px;color:var(--pa-text-1);font-size:12px;font-weight:700}.pa-account-head small{font-family:"JetBrains Mono",ui-monospace,Menlo,monospace;color:var(--pa-text-2)}.pa-account-row{display:grid;grid-template-columns:18px minmax(0,1fr);gap:10px;align-items:flex-start;padding:10px 8px;border-radius:8px;cursor:pointer}.pa-account-row:hover{background:var(--pa-bg-2)}.pa-account-row input{margin-top:2px;accent-color:var(--pa-accent)}.pa-account-row strong{display:block;color:var(--pa-text-0);font-size:12px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.pa-account-row small{display:block;color:var(--pa-text-2);font-size:11px;margin-top:2px}.pa-account-actions{display:flex;justify-content:flex-end;gap:8px;border-top:1px solid var(--pa-border);margin-top:8px;padding-top:10px}.pa-account-actions button{border-radius:7px;padding:7px 11px;font-size:12px;font-weight:700;cursor:pointer}.pa-account-cancel{background:transparent;color:var(--pa-text-2);border:1px solid var(--pa-border)}.pa-account-cancel:hover{color:var(--pa-text-0);border-color:var(--pa-border-strong)}.pa-account-save{background:var(--pa-text-0);color:var(--pa-bg-0);border:1px solid var(--pa-text-0)}.pa-account-save:hover{filter:brightness(.92)}
.pa-segment{display:inline-flex;align-items:center;background:var(--pa-bg-2);border:1px solid var(--pa-border);border-radius:8px;padding:3px;gap:2px}.pa-segment button,.pa-refresh,.pa-transfer-history{border:0;border-radius:6px;background:transparent;color:var(--pa-text-2);padding:6px 10px;font-size:12px;font-weight:600;cursor:pointer;white-space:nowrap}.pa-segment button:hover,.pa-refresh:hover,.pa-transfer-history:hover{color:var(--pa-text-0);background:var(--pa-bg-3)}.pa-segment button.active{background:var(--pa-bg-0);color:var(--pa-text-0);box-shadow:0 1px 2px rgba(0,0,0,.4)}.pa-refresh{background:var(--pa-accent);color:#0b0b0c;padding:9px 13px}.pa-transfer-history{border:1px solid var(--pa-border);background:var(--pa-bg-2)}.pa-transfer-history:disabled{opacity:.55;cursor:wait}
.pa-kpis{display:grid;grid-template-columns:1.45fr repeat(4,1fr);gap:1px;background:var(--pa-border);border:1px solid var(--pa-border);border-radius:14px;overflow:hidden;max-width:1440px;margin:0 auto 28px}.pa-kpi{background:var(--pa-bg-1);padding:18px 20px}.pa-kpi.primary{background:linear-gradient(135deg,var(--pa-bg-1),var(--pa-bg-2));box-shadow:inset 2px 0 0 var(--pa-accent)}.pa-kpi small{display:block;font-size:10px;letter-spacing:.12em;text-transform:uppercase;color:var(--pa-text-2);font-weight:700;margin-bottom:8px}.pa-kpi strong{display:block;font-family:"JetBrains Mono",ui-monospace,Menlo,monospace;font-size:22px;line-height:1;letter-spacing:-.03em;color:var(--pa-text-0)}.pa-kpi.primary strong{font-size:28px}.pa-kpi span{display:block;margin-top:8px;color:var(--pa-text-2);font-size:12px}
.pa-block{max-width:1440px;margin:28px auto 0}.pa-block-head{display:flex;align-items:flex-end;justify-content:space-between;gap:16px;margin-bottom:14px;flex-wrap:wrap}.pa-block h2{font-size:20px;font-weight:400;letter-spacing:-.02em;margin:0}.pa-block p{margin:2px 0 0;color:var(--pa-text-2);font-size:13px}
.pa-card{background:var(--pa-bg-1);border:1px solid var(--pa-border);border-radius:12px;padding:var(--pa-pad);overflow:hidden}.pa-card-title{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:16px;color:var(--pa-text-1);font-size:13px;font-weight:700}.pa-card-title small{color:var(--pa-text-3);font-size:10px;text-transform:uppercase;letter-spacing:.1em;font-weight:700;white-space:nowrap}
.pa-exposure-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:16px}.pa-exposure-row{display:grid;grid-template-columns:1.55fr 1.45fr;gap:16px;margin-top:16px}
.pa-pie-wrap{display:grid;grid-template-columns:116px minmax(0,1fr);gap:14px;align-items:center;margin-bottom:16px}.pa-pie{width:116px;height:116px;display:block;overflow:visible}.pa-pie-bg{fill:none;stroke:var(--pa-bg-3);stroke-width:18}.pa-pie-segment{fill:none;stroke-width:18;stroke-linecap:butt;transform:rotate(-90deg);transform-origin:50px 50px}.pa-pie-label{fill:var(--pa-text-0);font-family:"JetBrains Mono",ui-monospace,Menlo,monospace;font-size:12px;font-weight:700;text-anchor:middle}.pa-pie-sub{fill:var(--pa-text-3);font-size:7px;text-anchor:middle;text-transform:uppercase;letter-spacing:.08em}.pa-pie-summary{display:flex;flex-direction:column;gap:7px;min-width:0}.pa-pie-summary span{display:grid;grid-template-columns:9px minmax(0,1fr) 46px;gap:7px;align-items:center;color:var(--pa-text-2);font-size:11px}.pa-pie-summary i,.pa-pie-dot{display:inline-block;width:9px;height:9px;border-radius:2px}.pa-pie-summary strong{color:var(--pa-text-1);font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.pa-pie-summary em{font-style:normal;font-family:"JetBrains Mono",ui-monospace,Menlo,monospace;text-align:right;color:var(--pa-text-1)}.pa-pie-empty{margin-bottom:14px;padding:20px 12px}.pa-bars,.pa-bars-more-list{display:flex;flex-direction:column;gap:9px}.pa-bars-more{border-top:1px solid var(--pa-border);padding-top:9px}.pa-bars-more>summary{list-style:none;cursor:pointer;color:var(--pa-accent);font-size:12px;font-weight:700}.pa-bars-more>summary::-webkit-details-marker{display:none}.pa-bars-more>summary:before{content:"+";display:inline-block;width:13px;color:var(--pa-text-3);font-family:"JetBrains Mono",ui-monospace,Menlo,monospace}.pa-bars-more[open]>summary{margin-bottom:9px}.pa-bars-more[open]>summary:before{content:"-"}.pa-bar-detail{border-bottom:1px solid var(--pa-border);padding-bottom:8px}.pa-bar-detail:last-child{border-bottom:0;padding-bottom:0}.pa-bar-detail summary{list-style:none;cursor:pointer}.pa-bar-detail summary::-webkit-details-marker{display:none}.pa-bar-row{display:grid;grid-template-columns:minmax(0,1fr) 72px 52px;align-items:center;gap:10px;min-height:var(--pa-row);font-size:12px}.pa-bar-detail .pa-bar-name:before{content:"+";display:inline-block;width:13px;color:var(--pa-text-3);font-family:"JetBrains Mono",ui-monospace,Menlo,monospace}.pa-bar-detail[open] .pa-bar-name:before{content:"-"}.pa-bar-name{display:flex;align-items:center;gap:8px;color:var(--pa-text-1);font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.pa-bar-value{color:var(--pa-text-2);font-family:"JetBrains Mono",ui-monospace,Menlo,monospace;text-align:right}.pa-bar-pct,.pa-bar-row span:last-child,.pa-top-table .right{font-family:"JetBrains Mono",ui-monospace,Menlo,monospace;text-align:right}.pa-constituents{margin:4px 0 4px 14px;border-left:1px solid var(--pa-border);padding-left:10px}.pa-constituent{display:grid;grid-template-columns:58px minmax(0,1fr) 78px 48px;gap:8px;align-items:center;padding:5px 0;font-size:11px;color:var(--pa-text-2)}.pa-constituent-symbol{font-family:"JetBrains Mono",ui-monospace,Menlo,monospace;color:var(--pa-text-1);font-weight:700}.pa-constituent-name{white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.pa-constituent-value,.pa-constituent-pct{font-family:"JetBrains Mono",ui-monospace,Menlo,monospace;text-align:right;color:var(--pa-text-1)}.pa-legend{display:flex;gap:16px;border-top:1px solid var(--pa-border);margin-top:14px;padding-top:13px;color:var(--pa-text-2);font-size:11px}.pa-legend i{display:inline-block;width:12px;height:3px;border-radius:2px;margin-right:6px;vertical-align:middle}.pa-you{background:var(--pa-accent)}.pa-bench{background:var(--pa-text-2)}.pa-bench.dashed{height:0;border-top:1px dashed var(--pa-text-2);background:transparent}
.pa-stack{display:flex;height:46px;border:1px solid var(--pa-border);border-radius:8px;overflow:hidden;margin-bottom:16px}.pa-stack span{display:flex;align-items:center;justify-content:center;color:rgba(0,0,0,.72);font-family:"JetBrains Mono",ui-monospace,Menlo,monospace;font-size:11px;font-weight:700;border-right:1px solid rgba(0,0,0,.18);min-width:20px}.pa-stack .pa-rest{background:var(--pa-bg-3);color:var(--pa-text-1);border-right:0}
.pa-top-table{display:grid;grid-template-columns:24px 78px minmax(120px,1fr) 70px 90px;gap:0 10px;align-items:center;font-size:12px}.pa-top-table>span{padding:7px 0;border-bottom:1px solid var(--pa-border);min-width:0}.pa-top-table .pa-th{font-size:10px;letter-spacing:.1em;text-transform:uppercase;color:var(--pa-text-3);font-weight:700}.pa-rank{font-family:"JetBrains Mono",ui-monospace,Menlo,monospace;color:var(--pa-text-3)}.pa-symbol{font-family:"JetBrains Mono",ui-monospace,Menlo,monospace;font-weight:700}.pa-symbol i{display:inline-block;width:7px;height:7px;border-radius:99px;margin-right:8px}.pa-name{color:var(--pa-text-2);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.pa-correlation-card{min-width:0}.pa-matrix-wrap{width:100%;overflow:hidden}.pa-matrix{width:100%;min-width:100%;table-layout:fixed;border-collapse:separate;border-spacing:2px;font-family:"JetBrains Mono",ui-monospace,Menlo,monospace;font-size:10px}.pa-matrix th,.pa-matrix td{height:30px;text-align:center;padding:0;min-width:0}.pa-matrix thead th:first-child{width:44px}.pa-matrix th{color:var(--pa-text-2);font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.pa-matrix tbody th{text-align:right;padding-right:8px;color:var(--pa-text-1)}.pa-matrix td{border-radius:4px}.pa-matrix td.missing{background:var(--pa-bg-3);color:var(--pa-text-3)}
.pa-loading-state{min-height:178px;display:flex;align-items:center;justify-content:center;gap:10px;color:var(--pa-text-2);font-size:12px;border:1px dashed var(--pa-border);border-radius:10px;background:var(--pa-bg-2)}.pa-spinner{width:16px;height:16px;border-radius:999px;border:2px solid var(--pa-border-strong);border-top-color:var(--pa-accent-light);animation:pa-spin .8s linear infinite;display:inline-block;flex:0 0 auto}@keyframes pa-spin{to{transform:rotate(360deg)}}
.pa-greeks-card{padding:0}.pa-gauges{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:1px;background:var(--pa-border);border-bottom:1px solid var(--pa-border)}.pa-gauge{background:var(--pa-bg-1);padding:18px 14px;text-align:center}.pa-gauge small{display:block;color:var(--pa-text-2);font-size:10px;text-transform:uppercase;letter-spacing:.1em;font-weight:700}.pa-gauge svg{display:block;margin:5px auto 0;width:128px;height:78px;overflow:visible}.pa-gauge path,.pa-gauge line{fill:none;stroke-width:8;stroke-linecap:round}.pa-gauge path.track{stroke:var(--pa-bg-3)}.pa-gauge line{stroke:var(--pa-text-0);stroke-width:2}.pa-gauge circle{fill:var(--pa-text-0)}.pa-gauge strong{display:block;font-family:"JetBrains Mono",ui-monospace,Menlo,monospace;font-size:16px}.pa-gauge span{display:block;color:var(--pa-text-2);font-size:11px;margin-top:2px}.pa-greek-table-wrap{padding:var(--pa-pad)}.pa-greek-table{width:100%;font-size:12px;overflow-x:auto}.pa-greek-head,.pa-greek-summary,.pa-greek-position-row,.pa-greek-total{display:grid;grid-template-columns:minmax(220px,1.5fr) repeat(5,minmax(82px,.7fr));gap:10px;align-items:center;min-width:720px}.pa-greek-head{font-size:10px;text-align:right;color:var(--pa-text-3);letter-spacing:.1em;text-transform:uppercase;font-weight:700;padding:9px 10px;border-bottom:1px solid var(--pa-border)}.pa-greek-head span:first-child{text-align:left}.pa-greek-type-row{border-bottom:1px solid var(--pa-border)}.pa-greek-type-row summary{list-style:none;cursor:pointer}.pa-greek-type-row summary::-webkit-details-marker{display:none}.pa-greek-summary{padding:10px;color:var(--pa-text-1);font-family:"JetBrains Mono",ui-monospace,Menlo,monospace;text-align:right}.pa-greek-summary:hover{background:var(--pa-bg-2)}.pa-greek-label{font-family:Inter,-apple-system,sans-serif;font-weight:600;text-align:left}.pa-greek-label:before{content:"+";display:inline-block;width:13px;color:var(--pa-text-3);font-family:"JetBrains Mono",ui-monospace,Menlo,monospace}.pa-greek-type-row[open] .pa-greek-label:before{content:"-"}.pa-greek-table small{color:var(--pa-text-3);margin-left:6px}.pa-greek-position-list{border-top:1px solid var(--pa-border);background:var(--pa-bg-2);padding:4px 10px 6px}.pa-greek-position-row{padding:8px 0;color:var(--pa-text-2);font-family:"JetBrains Mono",ui-monospace,Menlo,monospace;text-align:right;border-bottom:1px solid rgba(255,255,255,.04)}.pa-greek-position-row:last-child{border-bottom:0}.pa-greek-position-name{text-align:left;font-family:Inter,-apple-system,sans-serif;min-width:0}.pa-greek-position-name strong{display:block;color:var(--pa-text-1);font-size:12px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.pa-greek-position-name small{display:block;margin:2px 0 0;color:var(--pa-text-3);font-size:11px}.pa-greek-empty{padding:12px;color:var(--pa-text-2);font-size:12px}.pa-greek-total{background:var(--pa-bg-2);border-top:1px solid var(--pa-border-strong);font-weight:700;padding:10px;font-family:"JetBrains Mono",ui-monospace,Menlo,monospace;text-align:right}.pa-greek-total span:first-child{text-align:left;font-family:Inter,-apple-system,sans-serif;color:var(--pa-text-1)}.pa-tag{display:inline-block;padding:2px 7px;border-radius:4px;margin-right:8px;text-transform:uppercase;letter-spacing:.08em;font-family:"JetBrains Mono",ui-monospace,Menlo,monospace;font-size:10px}.pa-tag.equity{background:rgba(63,178,127,.13);color:var(--pa-pos)}.pa-tag.option{background:var(--pa-accent-soft);color:var(--pa-accent-light)}.pa-tag.spread{background:rgba(124,142,205,.15);color:#8fa3d8}
.pa-pnl-card{overflow:visible}.pa-pnl-controls{display:flex;gap:10px;align-items:center;flex-wrap:wrap}.pa-pnl-stats{display:flex;gap:34px;flex-wrap:wrap;margin-bottom:14px}.pa-pnl-stats strong{display:block;font-family:"JetBrains Mono",ui-monospace,Menlo,monospace;font-size:20px}.pa-pnl-stats small{display:block;color:var(--pa-text-2);font-size:10px;letter-spacing:.1em;text-transform:uppercase}.pa-pnl-skeleton-stats{display:grid;grid-template-columns:repeat(4,minmax(120px,1fr));gap:18px;margin-bottom:16px}.pa-pnl-skeleton-stats span{height:42px;border-radius:8px;background:linear-gradient(90deg,var(--pa-bg-2),var(--pa-bg-3),var(--pa-bg-2));background-size:220% 100%;animation:pa-skeleton 1.2s ease-in-out infinite}.pa-chart-skeleton{width:100%;aspect-ratio:16/5.8;min-height:260px;border:1px dashed var(--pa-border);border-radius:10px;background:linear-gradient(180deg,rgba(201,162,39,.08),rgba(255,255,255,.015));display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;color:var(--pa-text-2);text-align:center}.pa-chart-skeleton strong{color:var(--pa-text-0);font-weight:600}.pa-chart-skeleton small{color:var(--pa-text-2);font-size:12px}@keyframes pa-skeleton{0%{background-position:120% 0}100%{background-position:-120% 0}}.pa-chart{width:100%;aspect-ratio:16/5.8;min-height:260px;position:relative}.pa-chart svg{width:100%;height:100%;display:block;overflow:visible}.pa-chart text{fill:var(--pa-text-2);font-family:"JetBrains Mono",ui-monospace,Menlo,monospace;font-size:10px}.pa-chart-overlay{position:absolute;inset:0 0 0 0;z-index:2;pointer-events:none}.pa-chart-hit{position:absolute;left:var(--x);top:0;width:14px;height:100%;transform:translateX(-50%);pointer-events:auto;outline:0;cursor:crosshair}.pa-chart-dot{position:absolute;left:50%;top:var(--y);width:9px;height:9px;border-radius:999px;background:var(--pa-accent-light);border:2px solid var(--pa-bg-0);box-shadow:0 0 0 4px rgba(201,162,39,.18);transform:translate(-50%,-50%);opacity:0;transition:opacity .12s ease}.pa-chart-hit:hover .pa-chart-dot,.pa-chart-hit:focus .pa-chart-dot,.pa-chart-hit.pinned .pa-chart-dot{opacity:1}.pa-chart.is-pinned .pa-chart-hit:not(.pinned){pointer-events:none}.pa-chart.is-pinned .pa-chart-hit.pinned{left:0;top:0;width:100%;height:100%;transform:none;z-index:5;pointer-events:none}.pa-chart.is-pinned .pa-chart-hit.pinned .pa-chart-dot{left:var(--x);top:var(--y)}.pa-chart.is-pinned .pa-chart-hit:not(.pinned):hover .pa-chart-dot,.pa-chart.is-pinned .pa-chart-hit:not(.pinned):focus .pa-chart-dot{opacity:0}.pa-chart-tooltip{position:absolute;left:50%;top:var(--y);width:min(560px,calc(100vw - 44px));max-height:min(420px,calc(100vh - 160px));display:flex;flex-direction:column;overflow:hidden;transform:translate(12px,-50%);background:rgba(17,17,19,.98);border:1px solid var(--pa-border-strong);border-radius:10px;box-shadow:0 18px 44px rgba(0,0,0,.52);padding:12px;opacity:0;visibility:hidden;transition:opacity .12s ease;pointer-events:none;cursor:auto}.pa-chart-hit.right .pa-chart-tooltip{left:auto;right:50%;transform:translate(-12px,-50%)}.pa-chart.is-pinned .pa-chart-hit.pinned .pa-chart-tooltip{left:var(--x);top:var(--y);transform:translate(12px,-50%)}.pa-chart.is-pinned .pa-chart-hit.pinned.right .pa-chart-tooltip{left:auto;right:calc(100% - var(--x));transform:translate(-12px,-50%)}.pa-chart-hit:hover .pa-chart-tooltip,.pa-chart-hit:focus .pa-chart-tooltip{opacity:1;visibility:visible}.pa-chart.is-pinned .pa-chart-hit:not(.pinned) .pa-chart-tooltip{opacity:0;visibility:hidden}.pa-chart-hit.pinned .pa-chart-tooltip{opacity:1;visibility:visible;pointer-events:auto}.pa-chart-tooltip-head{display:grid;grid-template-columns:1fr auto auto;gap:10px;align-items:center;border-bottom:1px solid var(--pa-border);padding-bottom:8px;margin-bottom:8px;flex:0 0 auto}.pa-chart-tooltip-head strong{font-size:12px}.pa-chart-tooltip-head span{font-family:"JetBrains Mono",ui-monospace,Menlo,monospace;color:var(--pa-text-0)}.pa-chart-tooltip-body{min-height:0;max-height:min(340px,calc(100vh - 236px));overflow-y:auto;overscroll-behavior:contain;-webkit-overflow-scrolling:touch;scrollbar-gutter:stable;scrollbar-width:thin;scrollbar-color:var(--pa-border-strong) var(--pa-bg-2);padding-right:4px}.pa-chart-tooltip-body::-webkit-scrollbar{width:10px}.pa-chart-tooltip-body::-webkit-scrollbar-track{background:var(--pa-bg-2);border-radius:999px}.pa-chart-tooltip-body::-webkit-scrollbar-thumb{background:var(--pa-border-strong);border:2px solid var(--pa-bg-2);border-radius:999px}.pa-chart-tooltip-body::-webkit-scrollbar-thumb:hover{background:var(--pa-text-3)}.pa-chart-unpin{display:none;border:1px solid var(--pa-border);background:var(--pa-bg-2);color:var(--pa-text-2);border-radius:6px;padding:3px 7px;font-size:10px;font-weight:700;cursor:pointer}.pa-chart-hit.pinned .pa-chart-unpin{display:inline-flex}.pa-chart-unpin:hover{color:var(--pa-text-0);border-color:var(--pa-border-strong)}.pa-chart-tooltip-grid{display:grid;grid-template-columns:minmax(150px,1.5fr) repeat(3,minmax(68px,.72fr));gap:6px 10px;align-items:center;font-size:11px}.pa-chart-tooltip-grid>span{font-family:"JetBrains Mono",ui-monospace,Menlo,monospace;text-align:right;white-space:nowrap}.pa-chart-tooltip-grid>span:nth-child(4n+1){text-align:left}.pa-chart-tooltip-grid>span:nth-child(-n+4){font-size:9px;color:var(--pa-text-3);text-transform:uppercase;letter-spacing:.09em;font-weight:700}.pa-chart-holding{min-width:0}.pa-chart-holding strong{display:block;color:var(--pa-text-1);font-family:Inter,-apple-system,sans-serif;font-size:11px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.pa-chart-holding small{display:block;margin-top:2px;color:var(--pa-text-3);font-size:10px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.pa-chart-tooltip-empty{color:var(--pa-text-2);font-size:12px;padding:6px 0}.pa-mark-note,.pa-transfer-note{border-top:1px solid var(--pa-border);margin-top:12px;padding-top:11px;color:var(--pa-text-2);font-size:12px}.pa-mark-note strong{color:var(--pa-text-1);font-weight:700}
.pa-pos,.pa-pos-text{color:var(--pa-pos)!important}.pa-neg,.pa-neg-text{color:var(--pa-neg)!important}.pa-alpha{color:var(--pa-accent-light)!important}.pa-muted,.pa-empty{color:var(--pa-text-2)}.pa-empty{text-align:center;padding:32px 12px;border:1px dashed var(--pa-border);border-radius:10px;background:var(--pa-bg-2)}
@media (max-width:1100px){.pa-kpis{grid-template-columns:1fr 1fr}.pa-exposure-grid{grid-template-columns:1fr 1fr}.pa-exposure-row{grid-template-columns:1fr}.pa-gauges{grid-template-columns:repeat(2,minmax(0,1fr))}}
@media (max-width:760px){.pa-shell{margin:-16px;padding:16px}.pa-appbar{display:block}.pa-actions{justify-content:flex-start;margin-top:16px}.pa-exposure-grid,.pa-kpis{grid-template-columns:1fr}.pa-bar-row{grid-template-columns:96px 1fr 48px}.pa-top-table{grid-template-columns:28px 96px 1fr;gap:0 8px}.pa-top-table>span:nth-child(5n+3),.pa-top-table>span:nth-child(5n+5){display:none}.pa-top-table .right{text-align:right}.pa-gauges{grid-template-columns:1fr}.pa-pnl-stats{gap:16px}.pa-chart{min-height:230px}}
`;
